/**
 * 학습진행 설정 페이지
 * 템플릿 관리 및 상품별 Configuration 설정 + 생각 정리하기 세트 관리
 * (간소화됨: Product → Class 계층만 사용)
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Settings, Plus, Edit2, Trash2, Copy, FileText, Target, Package, Loader2, X, Check, ChevronRight, AlertTriangle, CheckCircle2, GripVertical, BookOpen, Save, History, UploadCloud } from 'lucide-react'
import { ConfigurationBuilder } from '@/components/configuration'
import * as configApi from '@/api/configuration'
import * as diaryApi from '@/api/diaryQuizSet'
import { adminApi } from '@/api/admin'
import { handleApiError } from '@/api/client'
import type {
  ConfigurationTemplateResponse,
  LessonConfiguration,
  ConfigurationTemplateRequest
} from '@/types/configuration'
import { createDefaultConfiguration } from '@/types/configuration'
import ContentConfigMapping from '@/components/configuration-v2/ContentConfigMapping'
import { ConfigurationBuilderV2 } from '@/components/configuration-v2'
import type { LearningConfigurationV2 } from '@/types/configurationV2'
import { createDefaultLearningConfigurationV2 } from '@/types/configurationV2'
import type {
  DiaryQuizSetSummaryResponse,
  DiaryQuizSetResponse,
  DiaryQuizSetRequest,
  DiaryQuizSetItemRequest
} from '@/types/diaryQuizSet'

type Tab = 'templates' | 'content-mapping' | 'diary-quiz-sets'
type ViewMode = 'templates' | 'create' | 'edit'
type DiaryViewMode = 'list' | 'create' | 'edit'

// 드래그로 순서 변경 가능한 템플릿 카드
function SortableTemplateCard({
  template,
  onEdit,
  onDuplicate,
  onApply,
  onPublish,
  onVersions
}: {
  template: ConfigurationTemplateResponse
  onEdit: (t: ConfigurationTemplateResponse) => void
  onDuplicate: (t: ConfigurationTemplateResponse) => void
  onApply: (t: ConfigurationTemplateResponse) => void
  onPublish: (t: ConfigurationTemplateResponse) => void
  onVersions: (t: ConfigurationTemplateResponse) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: template.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const getLabel = (component: { type: string; location?: string }) => {
    if (component.type === 'quiz') {
      switch (component.location) {
        case 'pre': return '퀴즈(Pre)'
        case 'post': return '퀴즈(Post)'
        case 'wrapup': return '퀴즈(Wrapup)'
        case 'aiq': return 'AIQ 퀴즈'
        default: return '퀴즈'
      }
    }
    if (component.type === 'video') return '비디오'
    if (component.type === 'assignment') return '생각 정리하기'
    return component.type
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border border-gray-200 p-5 transition-shadow ${
        isDragging ? 'shadow-lg opacity-90 z-10' : 'hover:shadow-md'
      }`}
    >
      <div className="flex items-start gap-2 mb-3">
        <button
          type="button"
          className="touch-none p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-grab active:cursor-grabbing shrink-0"
          {...attributes}
          {...listeners}
          aria-label="순서 변경 드래그"
        >
          <GripVertical className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1 flex flex-col items-start">
          <h3 className="font-semibold text-gray-900 truncate w-full text-left">{template.name}</h3>
          {template.isSystemDefault && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded mt-1">
              시스템 기본
            </span>
          )}
          <div className="flex items-center gap-1 mt-1">
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded">
              발행 v{template.publishedVersion ?? 1}
            </span>
            {template.hasDraft && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                초안 v{template.draftVersion ?? (template.publishedVersion ?? 1) + 1}
              </span>
            )}
          </div>
        </div>
      </div>

      {template.description && (
        <p className="text-sm text-gray-500 mb-2 line-clamp-2">
          {template.description}
        </p>
      )}

      {(template.configuration as any)?.version === '2.0' ? (
        <div className="space-y-1 mb-4 text-xs">
          <p className="text-purple-600">🆕 v2 Configuration (5레벨 탭)</p>
          <p className={((template.configuration as any)?.completionRules?.lessonSequenceMode === 'flexible')
            ? 'text-green-600' : 'text-purple-600'
          }>
            {(template.configuration as any)?.completionRules?.lessonSequenceMode === 'flexible'
              ? '🔀 레슨 건너뛰기 가능' : '➡️ 레슨 순차 진행'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-1 mb-4 text-xs">
            {((template.configuration as any)?.productLearning?.startActivity?.enabled ||
              (template.configuration as any)?.productLearning?.endActivity?.enabled) && (
              <p className="text-orange-600">
                📦 상품 레벨:{' '}
                {[
                  (template.configuration as any)?.productLearning?.startActivity?.enabled &&
                    `시작활동(${((template.configuration as any)?.productLearning?.startActivity?.types || [])
                      .map((t: string) => t === 'quiz' ? 'AIQ' : 'Video')
                      .join(', ') || '-'})`,
                  (template.configuration as any)?.productLearning?.endActivity?.enabled &&
                    `마무리활동(${((template.configuration as any)?.productLearning?.endActivity?.types || [])
                      .map((t: string) => t === 'quiz' ? 'AIQ' : 'Video')
                      .join(', ') || '-'})`,
                ].filter(Boolean).join(', ')}
              </p>
            )}
            <p className={(template.configuration as any)?.completionRules?.lessonSequenceMode === 'flexible'
              ? 'text-green-600' : 'text-purple-600'
            }>
              {(template.configuration as any)?.completionRules?.lessonSequenceMode === 'flexible'
                ? '🔀 레슨 건너뛰기 가능' : '➡️ 레슨 순차 진행'}
            </p>
          </div>
          <div className="flex flex-wrap gap-1 mb-4">
            {(template.configuration as any)?.sequence
              ?.filter((c: any) => c.enabled)
              .map((component: any, idx: number) => (
                <span key={idx} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  {idx + 1}. {getLabel(component)}
                </span>
              ))}
          </div>
        </>
      )}

      <div className="pt-3 border-t border-gray-100 space-y-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onApply(template)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors"
          >
            <Target className="w-3.5 h-3.5" />
            상품에 적용하기
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onDuplicate(template)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          >
            <Copy className="w-3.5 h-3.5" />
            복제
          </button>
          <button
            onClick={() => onEdit(template)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" />
            상세보기 및 수정
          </button>
          <button
            onClick={() => onVersions(template)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
          >
            <History className="w-3.5 h-3.5" />
            버전
          </button>
        </div>
        {template.hasDraft && (
          <button
            onClick={() => onPublish(template)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-amber-600 hover:bg-amber-700 rounded transition-colors"
          >
            <UploadCloud className="w-3.5 h-3.5" />
            영향 확인 후 초안 발행
          </button>
        )}
      </div>
    </div>
  )
}

// 적용 대상 선택 모달 (Product만 지원 - 간소화됨)
interface ApplyTemplateModalProps {
  template: ConfigurationTemplateResponse
  onClose: () => void
  onSuccess: () => void
}

function ApplyTemplateModal({ template, onClose, onSuccess }: ApplyTemplateModalProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [isApplying, setIsApplying] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // 상품 목록 조회
  const { data: productsData, isLoading } = useQuery({
    queryKey: ['admin', 'products', 'all'],
    queryFn: () => adminApi.getProducts({ size: 100 })
  })

  const products = productsData?.content || []

  // 각 상품별 configuration 존재 여부 조회
  const configQueries = useQueries({
    queries: products.map((product) => ({
      queryKey: ['configuration', 'product', product.id],
      queryFn: () => configApi.getProductConfiguration(product.id),
      enabled: products.length > 0,
      staleTime: 60 * 1000, // 1분 캐시
    })),
  })

  // 상품별 configuration 존재 여부 맵
  const configStatusMap = new Map<number, { hasConfig: boolean; isLoading: boolean }>()
  products.forEach((product, index) => {
    const query = configQueries[index]
    configStatusMap.set(product.id, {
      hasConfig: query?.data != null,
      isLoading: query?.isLoading ?? true,
    })
  })

  const handleApply = async () => {
    if (!selectedId) {
      alert('적용할 상품을 선택해주세요.')
      return
    }

    setIsApplying(true)
    try {
      await configApi.setProductConfiguration(selectedId, template.configuration)
      const selected = products.find(p => p.id === selectedId)
      setSuccessMessage(`"${selected?.name}" 상품에 적용되었습니다.`)
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 1500)
    } catch (error) {
      console.error('Configuration 적용 실패:', error)
      alert('적용에 실패했습니다. 권한을 확인해주세요.')
    } finally {
      setIsApplying(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">템플릿 적용</h2>
              <p className="text-sm text-gray-500">{template.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 성공 메시지 */}
        {successMessage && (
          <div className="mx-5 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-700 font-medium">{successMessage}</span>
          </div>
        )}

        {/* 본문 */}
        {!successMessage && (
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* 상품 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-amber-600" />
                  상품 선택
                </div>
              </label>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  등록된 상품이 없습니다
                </div>
              ) : (
                <div className="max-h-[320px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {products.map((product) => {
                    const isSelected = selectedId === product.id
                    const configStatus = configStatusMap.get(product.id)

                    return (
                      <button
                        key={product.id}
                        onClick={() => setSelectedId(product.id)}
                        className={`w-full flex items-center justify-between p-3 text-left transition-colors ${
                          isSelected ? 'bg-amber-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className={`font-medium ${isSelected ? 'text-amber-700' : 'text-gray-700'}`}>
                            {product.name}
                          </span>
                          {/* Configuration 상태 표시 */}
                          {configStatus?.isLoading ? (
                            <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                          ) : configStatus?.hasConfig ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
                              <AlertTriangle className="w-3 h-3" />
                              설정 존재
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                              <CheckCircle2 className="w-3 h-3" />
                              신규
                            </span>
                          )}
                        </div>
                        <ChevronRight className={`w-4 h-4 ${isSelected ? 'text-amber-500' : 'text-gray-300'}`} />
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* 설명 */}
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              <p className="font-medium text-gray-700 mb-1">💡 Configuration 우선순위</p>
              <p>상품 → 클래스 순으로 병합되어 최종 설정이 결정됩니다.</p>
              <p className="mt-1 text-xs text-gray-500">Company Admin은 클래스에서 추가 커스터마이즈 가능</p>
            </div>
          </div>
        )}

        {/* 푸터 */}
        {!successMessage && (
          <div className="p-5 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleApply}
                disabled={!selectedId || isApplying}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isApplying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    적용 중...
                  </>
                ) : (
                  <>
                    <Target className="w-4 h-4" />
                    적용하기
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

// ========== 생각 정리하기 세트 카드 ==========

function SetCard({
  set,
  onEdit,
  onDelete
}: {
  set: DiaryQuizSetSummaryResponse
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 truncate">{set.name}</h3>
          {set.description && (
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{set.description}</p>
          )}
        </div>
        {set.isSystemDefault && (
          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex-shrink-0">
            기본
          </span>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
        <span>퀴즈 {set.itemCount}개</span>
        <span>생성: {new Date(set.createdAt).toLocaleDateString('ko-KR')}</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onEdit}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
        >
          <Edit2 className="w-3.5 h-3.5" />
          편집
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          삭제
        </button>
      </div>
    </div>
  )
}

// ========== 생각 정리하기 세트 생성/수정 폼 ==========

function DiaryQuizSetForm({
  initialData,
  onSaved,
  onCancel
}: {
  initialData: DiaryQuizSetResponse | null
  onSaved: () => void
  onCancel: () => void
}) {
  const isEdit = !!initialData

  const [name, setName] = useState(initialData?.name ?? '')
  const [description, setDescription] = useState(initialData?.description ?? '')
  const [items, setItems] = useState<DiaryQuizSetItemRequest[]>(
    (initialData?.items ?? []).map(item => ({
      quizId: typeof item.quizId === 'string' ? item.quizId : String(item.quizId),
      sequenceOrder: item.sequenceOrder
    }))
  )
  const [newQuizId, setNewQuizId] = useState('')
  const newQuizIdInputRef = useRef<HTMLInputElement>(null)

  const createMutation = useMutation({
    mutationFn: diaryApi.createDiaryQuizSet,
    onSuccess: onSaved
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: DiaryQuizSetRequest }) =>
      diaryApi.updateDiaryQuizSet(id, request),
    onSuccess: onSaved
  })

  const isSaving = createMutation.isPending || updateMutation.isPending
  const error = createMutation.error || updateMutation.error

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    const request: DiaryQuizSetRequest = {
      name: name.trim(),
      description: description.trim() || undefined,
      items: items.map((item, index) => ({
        quizId: String(item.quizId),
        sequenceOrder: index
      }))
    }

    if (isEdit && initialData) {
      updateMutation.mutate({ id: initialData.id, request })
    } else {
      createMutation.mutate(request)
    }
  }

  const addQuizItem = () => {
    const raw = (newQuizIdInputRef.current?.value ?? newQuizId).trim()
    if (!raw) return
    const quizId = raw
    setItems((prev) => {
      if (prev.some((item) => item.quizId === quizId)) return prev
      return [...prev, { quizId, sequenceOrder: prev.length }]
    })
    setNewQuizId('')
  }

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === items.length - 1) return

    const newItems = [...items]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]]
    setItems(newItems)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <button onClick={onCancel} className="hover:text-slate-700">생각 정리하기 세트</button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-slate-900 font-medium">{isEdit ? '세트 편집' : '새 세트 만들기'}</span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">기본 정보</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">이름 *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 기본 생각 정리하기 세트"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">설명</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="세트에 대한 설명을 입력하세요"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">퀴즈 목록</h2>
          <div className="flex items-center gap-2 mb-4">
            <input
              ref={newQuizIdInputRef}
              type="text"
              value={newQuizId}
              onChange={(e) => setNewQuizId(e.target.value)}
              placeholder="Quiz ID 입력"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addQuizItem()
                }
              }}
            />
            <button
              type="button"
              onClick={addQuizItem}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              추가
            </button>
          </div>

          {items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">
              퀴즈가 없습니다. Quiz ID를 입력하여 추가하세요.
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((item, index) => (
                <div
                  key={`${item.quizId}-${index}`}
                  className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-500 w-6 text-right flex-shrink-0">{index + 1}.</span>
                  <span className="text-sm font-medium text-slate-700 flex-1">Quiz #{String(item.quizId)}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveItem(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                      title="위로"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(index, 'down')}
                      disabled={index === items.length - 1}
                      className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                      title="아래로"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-1 text-red-400 hover:text-red-600"
                      title="삭제"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {handleApiError(error)}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={isSaving || !name.trim()}
            className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEdit ? '저장' : '생성'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            취소
          </button>
        </div>
      </form>
    </div>
  )
}

// ========== 생각 정리하기 세트 탭 콘텐츠 ==========

function DiaryQuizSetTab() {
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = useState<DiaryViewMode>('list')
  const [editingSet, setEditingSet] = useState<DiaryQuizSetResponse | null>(null)

  const { data: sets, isLoading } = useQuery({
    queryKey: ['diary-quiz-sets'],
    queryFn: diaryApi.getDiaryQuizSets
  })

  const deleteMutation = useMutation({
    mutationFn: diaryApi.deleteDiaryQuizSet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diary-quiz-sets'] })
    }
  })

  const handleEdit = async (setId: number) => {
    const detail = await diaryApi.getDiaryQuizSet(setId)
    setEditingSet(detail)
    setViewMode('edit')
  }

  const handleDelete = (setId: number, name: string) => {
    if (window.confirm(`"${name}" 세트를 삭제하시겠습니까?`)) {
      deleteMutation.mutate(setId)
    }
  }

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['diary-quiz-sets'] })
    setViewMode('list')
    setEditingSet(null)
  }

  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <DiaryQuizSetForm
        initialData={editingSet}
        onSaved={handleSaved}
        onCancel={() => {
          setViewMode('list')
          setEditingSet(null)
        }}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Diary-Text 퀴즈 세트를 관리합니다. 학습진행 설정에서 세트를 선택할 수 있습니다.
        </p>
        <button
          onClick={() => setViewMode('create')}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          새 세트 만들기
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : !sets || sets.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">퀴즈 세트가 없습니다</h3>
          <p className="text-sm text-slate-500 mb-6">새 세트를 만들어 퀴즈를 구성해보세요.</p>
          <button
            onClick={() => setViewMode('create')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            새 세트 만들기
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sets.map((set) => (
            <SetCard
              key={set.id}
              set={set}
              onEdit={() => handleEdit(set.id)}
              onDelete={() => handleDelete(set.id, set.name)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const ConfigurationManagement = () => {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<Tab>('templates')
  const [viewMode, setViewMode] = useState<ViewMode>('templates')
  const [selectedTemplate, setSelectedTemplate] = useState<ConfigurationTemplateResponse | null>(null)
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [pendingConfiguration, setPendingConfiguration] = useState<LessonConfiguration | null>(null)
  const [applyingTemplate, setApplyingTemplate] = useState<ConfigurationTemplateResponse | null>(null)
  const [orderedIds, setOrderedIds] = useState<number[]>([])
  const [useV2, setUseV2] = useState(true)
  const [pendingConfigurationV2, setPendingConfigurationV2] = useState<LearningConfigurationV2 | null>(null)
  const [templateOperationPending, setTemplateOperationPending] = useState(false)

  // 템플릿 목록 조회
  const { data: templateData, isLoading } = useQuery({
    queryKey: ['configurationTemplates'],
    queryFn: configApi.getTemplates
  })

  const templates = templateData?.templates || []

  // 서버에서 템플릿 로드 시 순서 초기화; 새로 추가된 템플릿은 뒤에 붙임
  useEffect(() => {
    if (templates.length === 0) return
    setOrderedIds((prev) => {
      const known = prev.filter((id) => templates.some((t) => t.id === id))
      const newIds = templates.filter((t) => !prev.includes(t.id)).map((t) => t.id)
      if (known.length === 0 && newIds.length === 0) return templates.map((t) => t.id)
      return known.length ? [...known, ...newIds] : templates.map((t) => t.id)
    })
  }, [templates])

  // 드래그 후 표시할 순서: orderedIds 기준 + 목록에 없는 id 제외, 새 템플릿은 뒤에
  const displayTemplates = useMemo(() => {
    if (orderedIds.length === 0) return templates
    const byId = new Map(templates.map((t) => [t.id, t]))
    const ordered = orderedIds.filter((id) => byId.has(id)).map((id) => byId.get(id)!)
    const appended = templates.filter((t) => !orderedIds.includes(t.id))
    return [...ordered, ...appended]
  }, [templates, orderedIds])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const reorderMutation = useMutation({
    mutationFn: (templateIds: number[]) => configApi.reorderTemplates(templateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurationTemplates'] })
    },
    onError: (error: Error) => {
      console.error('템플릿 순서 저장 실패:', error)
      alert(`순서 저장에 실패했습니다: ${error.message || '서버 오류가 발생했습니다.'}`)
    }
  })

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (over == null || active.id === over.id) return
      setOrderedIds((prev) => {
        const oldIndex = prev.indexOf(active.id as number)
        const newIndex = prev.indexOf(over.id as number)
        if (oldIndex === -1 || newIndex === -1) return prev
        const next = arrayMove(prev, oldIndex, newIndex)
        reorderMutation.mutate([...next])
        return next
      })
    },
    [reorderMutation]
  )

  // 템플릿 생성
  const createMutation = useMutation({
    mutationFn: (request: ConfigurationTemplateRequest) => configApi.createTemplate(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurationTemplates'] })
      resetForm()
    },
    onError: (error: Error) => {
      console.error('템플릿 생성 실패:', error)
      alert(`템플릿 생성에 실패했습니다: ${error.message || '서버 오류가 발생했습니다.'}`)
    }
  })

  // 템플릿 수정
  const updateMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: ConfigurationTemplateRequest }) =>
      configApi.updateTemplate(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurationTemplates'] })
      alert('초안으로 저장했습니다. 목록에서 영향 범위를 확인한 뒤 발행해주세요.')
      resetForm()
    },
    onError: (error: Error) => {
      console.error('템플릿 수정 실패:', error)
      alert(`템플릿 수정에 실패했습니다: ${error.message || '서버 오류가 발생했습니다.'}`)
    }
  })

  // 템플릿 삭제
  const deleteMutation = useMutation({
    mutationFn: configApi.deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurationTemplates'] })
      resetForm()
    }
  })

  const resetForm = () => {
    setViewMode('templates')
    setSelectedTemplate(null)
    setTemplateName('')
    setTemplateDescription('')
    setPendingConfiguration(null)
    setPendingConfigurationV2(null)
    setUseV2(true)
  }

  const handleCreateNew = () => {
    setViewMode('create')
    setTemplateName('')
    setTemplateDescription('')
    setUseV2(true)
    setPendingConfiguration(createDefaultConfiguration())
    setPendingConfigurationV2(createDefaultLearningConfigurationV2())
  }

  const handleEdit = (template: ConfigurationTemplateResponse) => {
    setViewMode('edit')
    setSelectedTemplate(template)
    setTemplateName(template.name)
    setTemplateDescription(template.description || '')
    const editableConfiguration = template.draftConfiguration ?? template.configuration
    // v2 감지: 미발행 초안이 있으면 초안을 우선 편집
    const isV2 = (editableConfiguration as any)?.version === '2.0'
    setUseV2(isV2)
    if (isV2) {
      setPendingConfigurationV2(editableConfiguration as unknown as LearningConfigurationV2)
      setPendingConfiguration(null)
    } else {
      setPendingConfiguration(editableConfiguration as LessonConfiguration)
      setPendingConfigurationV2(null)
    }
  }

  const handleDuplicate = (template: ConfigurationTemplateResponse) => {
    setViewMode('create')
    setTemplateName(`${template.name} (복사본)`)
    setTemplateDescription(template.description || '')
    setPendingConfiguration(template.configuration)
  }

  const handleDelete = async (template: ConfigurationTemplateResponse) => {
    if (!confirm(`'${template.name}' 템플릿을 삭제하시겠습니까?`)) return
    deleteMutation.mutate(template.id)
  }

  const handlePublish = async (template: ConfigurationTemplateResponse) => {
    if (!template.hasDraft || templateOperationPending) return
    setTemplateOperationPending(true)
    try {
      const impact = await configApi.getTemplateImpact(template.id)
      const confirmed = confirm(
        `'${template.name}' 초안 v${template.draftVersion}을 발행하시겠습니까?\n\n` +
        `콘텐츠 매핑 ${impact.contentMappingCount}개, 단일 템플릿 상품 ${impact.singleTemplateProductCount}개에 즉시 적용됩니다.`,
      )
      if (!confirmed) return
      await configApi.publishTemplate(template.id)
      await queryClient.invalidateQueries({ queryKey: ['configurationTemplates'] })
      alert(`v${template.draftVersion} 발행을 완료했습니다.`)
    } catch (error) {
      console.error('템플릿 발행 실패:', error)
      alert(`템플릿 발행에 실패했습니다: ${error instanceof Error ? error.message : '서버 오류가 발생했습니다.'}`)
    } finally {
      setTemplateOperationPending(false)
    }
  }

  const handleVersions = async (template: ConfigurationTemplateResponse) => {
    if (templateOperationPending) return
    setTemplateOperationPending(true)
    try {
      const versions = await configApi.getTemplateVersions(template.id)
      if (versions.length === 0) {
        alert('저장된 발행 버전이 없습니다.')
        return
      }
      const versionList = versions
        .map((version) => `v${version.versionNumber} · ${new Date(version.createdAt).toLocaleString()}`)
        .join('\n')
      const selected = prompt(
        `발행 이력\n${versionList}\n\n초안으로 복원할 버전 번호를 입력하세요. 취소하려면 비워두세요.`,
      )
      if (!selected?.trim()) return
      const versionNumber = Number(selected)
      if (!Number.isInteger(versionNumber) || !versions.some((version) => version.versionNumber === versionNumber)) {
        alert('목록에 있는 버전 번호를 입력해주세요.')
        return
      }
      if (!confirm(`v${versionNumber} 설정을 새 초안으로 복원하시겠습니까? 발행 전까지 상용에는 적용되지 않습니다.`)) return
      await configApi.rollbackTemplateToDraft(template.id, versionNumber)
      await queryClient.invalidateQueries({ queryKey: ['configurationTemplates'] })
      alert(`v${versionNumber} 설정을 초안으로 복원했습니다. 검토 후 발행해주세요.`)
    } catch (error) {
      console.error('템플릿 버전 조회/복원 실패:', error)
      alert(`버전 작업에 실패했습니다: ${error instanceof Error ? error.message : '서버 오류가 발생했습니다.'}`)
    } finally {
      setTemplateOperationPending(false)
    }
  }

  const handleConfigurationChange = useCallback((configuration: LessonConfiguration) => {
    setPendingConfiguration(configuration)
  }, [])

  const handleSubmit = () => {
    const configToSave = useV2 ? pendingConfigurationV2 : pendingConfiguration
    if (!templateName.trim() || !configToSave) {
      alert('템플릿 이름과 설정을 입력해주세요.')
      return
    }

    const request: ConfigurationTemplateRequest = {
      name: templateName.trim(),
      description: templateDescription.trim() || undefined,
      configuration: configToSave as ConfigurationTemplateRequest['configuration']
    }

    if (viewMode === 'edit' && selectedTemplate) {
      updateMutation.mutate({ id: selectedTemplate.id, request })
    } else {
      createMutation.mutate(request)
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">학습진행 설정</h1>
                <p className="text-sm text-gray-500">학습 순서와 옵션을 설정하는 템플릿을 관리합니다</p>
              </div>
            </div>

            {activeTab === 'templates' && viewMode === 'templates' && (
              <button
                onClick={handleCreateNew}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                새 템플릿
              </button>
            )}

            {activeTab === 'templates' && viewMode !== 'templates' && (
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                목록으로
              </button>
            )}
          </div>

          {/* 탭 */}
          <div className="flex gap-1 mt-4 -mb-4">
            <button
              onClick={() => { setActiveTab('templates'); resetForm() }}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                activeTab === 'templates'
                  ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                템플릿 관리
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('content-mapping'); resetForm() }}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                activeTab === 'content-mapping'
                  ? 'border-purple-500 text-purple-600 bg-purple-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Content-Config 매핑
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('diary-quiz-sets'); resetForm() }}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                activeTab === 'diary-quiz-sets'
                  ? 'border-amber-500 text-amber-600 bg-amber-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                생각 정리하기 세트
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Content-Config 매핑 탭 */}
        {activeTab === 'content-mapping' && (
          <ContentConfigMapping />
        )}

        {/* 생각 정리하기 세트 탭 */}
        {activeTab === 'diary-quiz-sets' && (
          <DiaryQuizSetTab />
        )}

        {/* 템플릿 목록 */}
        {activeTab === 'templates' && viewMode === 'templates' && (
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-12 text-gray-500">로딩 중...</div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">등록된 템플릿이 없습니다.</p>
                <button
                  onClick={handleCreateNew}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  첫 템플릿 만들기
                </button>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={displayTemplates.map((t) => t.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {displayTemplates.map((template) => (
                      <SortableTemplateCard
                        key={template.id}
                        template={template}
                        onEdit={handleEdit}
                        onDuplicate={handleDuplicate}
                        onApply={setApplyingTemplate}
                        onPublish={handlePublish}
                        onVersions={handleVersions}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        )}

        {/* 생성/수정 폼 */}
        {activeTab === 'templates' && (viewMode === 'create' || viewMode === 'edit') && (
          <div className="space-y-6">
            {/* 템플릿 메타 정보 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {viewMode === 'create' ? '새 템플릿' : '템플릿 수정'}
              </h3>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    템플릿 이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="예: B2C 기본, 기업 교육용"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                  <input
                    type="text"
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder="템플릿에 대한 간단한 설명"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Version Toggle */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Configuration 버전:</span>
                <label className="flex items-center gap-1.5 text-sm">
                  <input type="radio" name="configVersion" checked={!useV2} onChange={() => { setUseV2(false); setPendingConfigurationV2(null) }} />
                  v1 (기존)
                </label>
                <label className="flex items-center gap-1.5 text-sm">
                  <input type="radio" name="configVersion" checked={useV2} onChange={() => { setUseV2(true); setPendingConfigurationV2(createDefaultLearningConfigurationV2()) }} />
                  v2 (5레벨 탭)
                </label>
              </div>
            </div>

            {/* Configuration Builder */}
            {!useV2 && pendingConfiguration && (
              <ConfigurationBuilder
                initialConfiguration={pendingConfiguration}
                onChange={handleConfigurationChange}
                showPreview={true}
              />
            )}
            {useV2 && (
              <ConfigurationBuilderV2
                initialConfiguration={pendingConfigurationV2 ?? createDefaultLearningConfigurationV2()}
                onChange={(config) => setPendingConfigurationV2(config)}
              />
            )}

            {/* 저장 버튼 */}
            <div className="flex justify-between gap-3">
              <div>
                {viewMode === 'edit' && selectedTemplate && (
                  <button
                    type="button"
                    onClick={() => handleDelete(selectedTemplate)}
                    disabled={deleteMutation.isPending}
                    className="flex items-center gap-1.5 px-6 py-2 text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {deleteMutation.isPending ? '삭제 중...' : '삭제'}
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={resetForm}
                  className="px-6 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !templateName.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? '저장 중...' : viewMode === 'create' ? '템플릿 생성' : '초안 저장'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 템플릿 적용 모달 */}
      {applyingTemplate && (
        <ApplyTemplateModal
          template={applyingTemplate}
          onClose={() => setApplyingTemplate(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['configurationTemplates'] })
          }}
        />
      )}
    </div>
  )
}
